import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lock } from "lucide-react";

const STORAGE_KEY = "mn_guest_unlocked_v2";
const EXPECTED = (process.env.REACT_APP_GUEST_PASSWORD || "").trim().toLowerCase();
const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/2cf1c386-8bf6-4b35-9508-22a3e6aba20d/images/17a40a52cb5a9617d4db98ef89565c9b1178c04c1c477082ca43eed98068840b.png";

export default function GuestGate({ children }) {
  const location = useLocation();
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch (_) {
      return false;
    }
  });
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!unlocked && EXPECTED && !location.pathname.startsWith("/admin")) {
      document.title = "Michelle & Niall · 4 June 2027";
    }
  }, [unlocked, location.pathname]);

  // /admin has its own auth — bypass the guest gate
  if (location.pathname.startsWith("/admin")) {
    return children;
  }

  // If no password is configured, just render children
  if (!EXPECTED) return children;

  if (unlocked) return children;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim().toLowerCase() === EXPECTED) {
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* ignore */ }
      setUnlocked(true);
    } else {
      setError("Hmm, that's not quite right. Check the back of your invitation.");
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 overflow-hidden">
      <img
        src={HERO_IMG}
        alt="Glenlo Abbey, Galway"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#1A1818]/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1818]/40 via-[#1A1818]/30 to-[#6A1E2D]/70" />

      <div className="relative z-10 w-full max-w-md text-center text-[#FAF9F6]">
        <Lock className="w-6 h-6 text-[#A3C1DA] mx-auto mb-6" strokeWidth={1.4} />
        <p
          data-testid="guest-gate-overline"
          className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] mb-4"
        >
          A private invitation
        </p>
        <h1 className="font-display font-light text-5xl md:text-6xl leading-[0.95] mb-4">
          Michelle <span className="italic text-[#A3C1DA]">&amp;</span> Niall
        </h1>
        <p className="font-display italic text-[#FAF9F6]/85 text-base md:text-lg mb-10">
          Please enter the password from your invitation.
        </p>

        <form
          onSubmit={handleSubmit}
          data-testid="guest-gate-form"
          className="space-y-6"
        >
          <input
            type="text"
            autoFocus
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(""); }}
            data-testid="guest-gate-input"
            className="field-underline-dark text-center text-lg tracking-[0.2em]"
            placeholder="password"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {error && (
            <p data-testid="guest-gate-error" className="text-sm text-[#A3C1DA]">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="guest-gate-submit"
            className="inline-flex items-center justify-center gap-3 bg-[#FAF9F6] text-[#6A1E2D] hover:bg-[#A3C1DA] hover:text-[#1A1818] transition-colors duration-300 px-12 py-4 text-xs tracking-luxe uppercase"
          >
            Enter
            <span aria-hidden>→</span>
          </button>
        </form>

        <p className="text-[#FAF9F6]/55 text-xs mt-10">
          4<sup className="text-[8px]">th</sup> June 2027 · Glenlo Abbey, Galway
        </p>
      </div>
    </div>
  );
}
